import { ApiVersionEnum, LogLevel, PmsType } from "../../tenant";
import { KioskConfig } from "./kiosk-config";
import { InputProps } from "../../components";
import { SecondScreenDevicesConfig } from "./second-screen-devices-config";
import { ChipProps } from "../../components/chip/chip.types";

export interface AdyenConfig {
  clientKey: string;
  environment: string;
}

export interface PrismicConfig {
  apiEndpoint: string;
  accessToken: string;
}

// backoffice specific
export interface FeatureFlagsBackoffice {
  reservationTableEnabled: boolean;
  tasksEnabled: boolean;
  unifiedMessagingEnabled: boolean;
  breakfastViewEnabled: boolean;
  maintenanceEnabled: boolean;
  guestListEnabled: boolean;
  housekeepingV2Enabled: boolean;
  checkinWizardEnabled: boolean;
  billingTabEnabled: boolean;
}

export interface FeatureFlags extends FeatureFlagsBackoffice {
  authEnabled: boolean;
  bookingAppIntegrated: boolean;
  enableExtendStay: boolean; // Enable feature to extend stay
  shopEnabled: boolean; // Enable food & drinks shop
  searchEnabled: boolean; // IBE
  hohDemoEnabled: boolean;
  cookiesEnabled: boolean; // Cookies hint
  searchByLocationEnabled: boolean; // booking engine specific
  multiPropertyBookingEnabled: boolean;
  bookedServicesEnabled: boolean; // Enable booked services page
  showVat: boolean;
  boxEnabled: boolean; // Shop box enabled
  disableSVGThemeOverriding: boolean; // Disable default override of the svg icons from prismic
  documentsUploadEnabled: boolean; // Enable Configurable passport/id
  payFromBookingOverviewDisabled: boolean; // Disable feature to pay form booking-overview page
  diverseGenderEnabled: boolean; // Enable gender diverse option
  tfeFeatureEnabled: boolean; //are tfe specific features enabled
  imsFeatureEnabled: boolean; //are ims specific features enabled
  arrivalsWithoutInHouseEnabled: boolean; // Arrival Backoffice without Inhouse
  reverseDisplaySearchUnitGroupPageEnabled: boolean; // Reverse the display of units and property description
  originalImageSizeForPropertyFeaturesEnabled: boolean; // Displays original size of image for property features
  payAtCheckoutEnabled: boolean; // Do not force people to pay in GJ, payment could be done at the end
  selfPouringStationEnabled: boolean; // Shop self pouring station enabled
  privateDoorsDisabled: boolean; // Hide all private doors regardless of the door provider (do not mind typo)
  payAtCheckinEnabled: boolean; // Do not force people to pay in GJ in the moment of the reservation, they can pay in the checkin
  marketingConsentEnabled: boolean; // For checkbox on GJ to allow sending marketing news
  marketingConsentDoubleOptinEnabled: boolean; // For double option on GJ for the Marketing Consent
  ibeOnly: boolean; // Only expose IBE
  housekeepingV2Enabled: boolean; // To display housekeepingV2
  travelBuddyIsMinorValidationEnabled: boolean; // To have validation in the flow and myStay if travelBuddies are minors (under 18)
  checkoutFlowEnabled: boolean; // Enables checkout flow for the guests [gj]
  addToHomeScreenHidden: boolean; // Hide the add to home screen fixed tip on GJ;
  guestCheckInIntoPMSDisabled: boolean; // Hide the guest check in button on GJ
  secondScreenEnabled: boolean; //Controlls second screen feature on both app
  genderDisplayDisabled: boolean; // Hide gender input on all forms in GJ
  findReservationByConfirmationNumberEnabled: boolean; // Feature to find a reservation by confirmationNumber in GJ
}

export interface LocaleMappingConfig {
  [key: string]: string;
}

export interface PropertyConfig {
  baseUrls?: Array<string>;
  frontend?: PropertyFrontendConfigWithDoor;
  saltoKS?: SaltoKS; // provider SALTO
  saltoSpace?: CommonEncoders; // provider SALTO_SPACE_MOBILE_KEY, SALTO_SPACE_ONLINE
  aaVisionline?: CommonEncoders; // provider AA_VISIONLINE
  aaVostio?: CommonEncoders; // provider AA_VISIONLINE
  fourSuites?: CommonEncoders; // provider FOUR_SUTIES
  hafele?: CommonEncoders; // provider HAFELE
  glutz?: CommonEncoders; // provider Glutz
  dormaKaba?: CommonEncoders; // provider Glutz
}

export interface PropertyFrontendConfigWithDoor extends PropertyFrontendConfig {
  doorConfig?: DoorConfig;
}

export interface PropertyFrontendConfig {
  features?: FeatureFlags;
  hidePrepaymentConfig?: HidePrepaymentConfig;
  locale?: LocaleMappingConfig;
  theme?: string;
  pwa?: PWA;
  earliestCheckinOffset?: number;
  allowedLocales?: string[];
  allowedBackofficeLanguages?: string[];
  allowedLanguages?: string[];
  search?: SearchConfig;
  preferredCountries?: string[];
  toneOfVoice?: string;
  disabledCountriesForIdentification?: string[];
  disabledCountriesForPassportUpload?: string[];
  themeOverride?: ThemeOverride;
  secondScreenDevices?: Record<string, SecondScreenDevicesConfig>;
  additionalTravelBuddyFields?: AdditionalTravelBuddyFieldsConfig;
}

export interface HidePrepaymentConfig {
  [key: string]: boolean; // key is PrepaymentType on BE
}

export interface SearchConfig {
  defaultArrivalDateDelta?: number;
  defaultStayLength?: number;
  defaultAdults?: number;
  defaultChildrenAges?: number[];
  adultsOnly?: boolean;
  childrenAgesConfig?: { minAge: number; maxAge: number };
  maxReservationsPerBooking?: number;
  deltaEndOfDayProcessing?: string; //We are going to use ISO8601 example: "PT4H"
}

export interface ThemeOverride {
  input: {
    variant: InputProps["variant"];
  };
  chip: {
    variant: ChipProps["variant"];
  };
}

export interface DoorProviderDevice {
  [key: string]: {
    name: string;
  };
}

export interface SaltoKS {
  siteId: string;
  tagReaders: DoorProviderDevice;
}

export interface CommonEncoders {
  encoders: DoorProviderDevice;
}

export interface KeycloakConfig {
  realm: string;
  "auth-server-url": string;
  "ssl-required": string;
  resource: string;
  "public-client": boolean;
  "verify-token-audience": boolean;
  "use-resource-role-mappings": boolean;
  "confidential-port": number;
}

interface PWAIcon {
  favicon: string;
  logoMedium: string;
  logoBig: string;
  maskable: string;
}

export interface PWA {
  shortName: string;
  name: string;
  icons: PWAIcon;
  themeColor: string;
  backgroundColor: string;
}

export interface TimeConfig {
  hours: number;
  minutes: number;
}

export interface KeycloakNativeConfig {
  androidReturnURL: string;
  iosReturnURL: string;
}

export interface TenantConfig extends PropertyFrontendConfig {
  tenantId: string;
  keycloak: KeycloakConfig;
  prismic?: PrismicConfig;
  likemagicPrismic?: PrismicConfig;
  adyen?: AdyenConfig;
  features?: FeatureFlags;
  allowedBackofficeLanguages?: string[];
  allowedLanguages?: string[];
  apiKey?: string;
  gtmId?: string;
  urls: {
    bookingAppUrl: string;
    apaleoUrl: string;
    zeebeUrl: string;
    magicSupportUrl: string;
    magicSuggestFeatureUrl: string;
    messagebirdUrl: string;
    pmsUrl: string;
  };
  routesWithAuthToken?: string[];
  googleMaps?: {
    apiKey?: string;
  };
  enabledCommunicationChannels: CommunicationChannel[];
  additionalTravelBuddyFields: AdditionalTravelBuddyFieldsConfig;
  properties?: { [propertyId: string]: PropertyConfig };
  keycloakNative?: KeycloakNativeConfig;
  version?: ApiVersionEnum;
  logLevel?: LogLevel;
  pms?: PmsType;
  kiosk?: Record<string, KioskConfig>;
  secondScreenDevices?: Record<string, SecondScreenDevicesConfig>;
}

export interface DoorConfig {
  doorProvider: DoorProvider;
  batteryLevelSupported: boolean;
  mainDoorPrefixes: string[];
  timeAvailability: {
    publicDoorsAvailableFrom: TimeConfig;
    publicDoorsAvailableTo: TimeConfig;
  };
}

export enum DoorProvider {
  SALTO = "SALTO",
  SALTO_SPACE_MOBILE_KEY = "SALTO_SPACE_MOBILE_KEY",
  SALTO_SPACE_ONLINE = "SALTO_SPACE_ONLINE",
  OFFLINE_PIN = "OFFLINE_PIN",
  BURGWACHTER_LEGACY = "BURGWACHTER_LEGACY",
  BURGWACHTER = "BURGWACHTER",
  HAFELE = "HAFELE",
  FOUR_SUITES = "FOUR_SUITES",
  AA_VISIONLINE = "AA_VISIONLINE",
  AA_VOSTIO = "AA_VOSTIO",
  GLUTZ = "GLUTZ",
  OFFLINE_KEY = "OFFLINE_KEY",
  NUKI = "NUKI",
  DORMA_KABA = "DORMA_KABA",
  NONE = "NONE"
}

export enum CommunicationChannel {
  SMS = "SMS",
  EMAIL = "EMAIL",
  WHATSAPP = "WHATSAPP"
}

interface AdditionalTravelBuddyFieldConfig {
  required: boolean;
}

export interface AdditionalTravelBuddyFieldsConfig {
  addressLine1?: AdditionalTravelBuddyFieldConfig;
  city?: AdditionalTravelBuddyFieldConfig;
  countryCode?: AdditionalTravelBuddyFieldConfig;
  dateOfBirth?: AdditionalTravelBuddyFieldConfig;
  nationalityCountryCode?: AdditionalTravelBuddyFieldConfig;
  postalCode?: AdditionalTravelBuddyFieldConfig;
}
